import React, { Suspense, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import { values, prop } from 'ramda'
import {
  APP,
  NOTIFICATION_ERROR,
  NOTIFICATION_SUCCESS,
  NOTIFICATION_WARNING,
  SHOW_FAVOURITE_VEHICLES,
  SHOW_VEHICLE_SNAPSHOTS,
} from 'utils/constants'
import {
  ECP_PENDING_UPDATE,
  ECP_PENDING_MULTIPLE_UPDATES,
  OPTIONAL_SOLENOIDS_NOT_INSTALLED,
  SNG_PENDING_UPDATE,
} from 'utils/notifications'
import * as ROUTES from 'constants/routes'
import useInterval from 'hooks/useInterval'
import { useMultiApi } from 'hooks/useApi'
import { usePrevious } from 'hooks/index'

import { useHistory, useLocation } from 'react-router'

import {
  getShowReleaseNotes,
  getIsProcessingFavoritedVehicle,
} from 'selectors/user'
import { getAllNotifications } from 'selectors/application'
import {
  getAPIVersion,
  createSignals,
  deleteSystemNotification,
} from 'actions/application'
import {
  requestUserFavoriteVehicles,
  requestRecentlyViewedData,
  requestAlert,
  getUser,
  setFavoriteVehiclesFlyoutVisible,
} from 'actions/user'
import { setSelectedIdWithDrawer } from 'actions/navigator'

import 'styles/ReactTable.css'
import 'styles/App.css'

import { Loading } from 'components/patterns'
import ErrorBoundary from 'components/ErrorBoundary'
import { pageView } from 'utils/analytics'
import { IconContext } from 'react-icons'
import Routes from 'components/Routes'
import { useDynamicTitle } from 'hooks/useDynamicTitle'
import { NotificationProvider } from 'context/notifications'
import { AppRoot } from './App.style'

const ALERT_CHECK_INTERVAL = 5 * 60 * 1000

// Notifications that should persist across route changes (until programmed to DiagnosticLink)
const PERSISTENT_NOTIFICATION_IDS = [
  ECP_PENDING_UPDATE,
  ECP_PENDING_MULTIPLE_UPDATES,
  OPTIONAL_SOLENOIDS_NOT_INSTALLED,
  SNG_PENDING_UPDATE,
]

const fetchActions = [
  getUser,
  getAPIVersion,
  requestUserFavoriteVehicles,
  requestRecentlyViewedData,
  requestAlert,
]

const makeFullPath = (locObj) =>
  `${prop('pathname', locObj)}${prop('search', locObj)}`

const App = () => {
  const history = useHistory()
  const dispatch = useDispatch()

  const [shouldFetch, setShouldFetch] = useState(true)

  const showReleaseNotes = useSelector(getShowReleaseNotes)

  // True while a favorite add/remove is in flight (covers both operations).
  // The user reducer sets processingFavoritedVehicle on FAVORITING_USER_VEHICLE
  // and REMOVING_FAVORITED_USER_VEHICLE, and clears it on success/failure.
  const isLoading = useSelector(getIsProcessingFavoritedVehicle)

  const notifications = useSelector(getAllNotifications)

  const [{ doneLoading, error }, doFetch] = useMultiApi(fetchActions)

  useInterval(() => {
    dispatch(requestAlert())
  }, ALERT_CHECK_INTERVAL)

  useEffect(() => {
    if (shouldFetch) {
      doFetch()
      setShouldFetch(false)
    }

    if (doneLoading && showReleaseNotes) history.push(ROUTES.releaseNotes)
  }, [shouldFetch, doFetch, showReleaseNotes, doneLoading, history])

  const signals = values(
    createSignals({
      getUser: getUser(),
      getAPIVersion: getAPIVersion(),
    })
  )

  const location = useLocation()
  const { action: histAction } = history
  const currPath = makeFullPath(location)
  const prevPath = usePrevious(currPath)
  const currKey = location.key
  const prevKey = usePrevious(currKey)

  // Get the navigator selectedId to check if favorites drawer is open
  const selectedId = useSelector((state) =>
    state.getIn(['navigator', 'selectedId'])
  )

  // Close all favorite vehicles and snapshot panels on any route change,
  // including same-URL navigations (location.key changes even when path stays the same)
  useEffect(() => {
    if (currPath !== prevPath || currKey !== prevKey) {
      // Close the flyout (used in VehicleSearch)
      dispatch(setFavoriteVehiclesFlyoutVisible(false))
      // Close the navigator drawer (used in vehicle summary pages)
      if (
        selectedId === SHOW_FAVOURITE_VEHICLES ||
        selectedId === SHOW_VEHICLE_SNAPSHOTS
      ) {
        dispatch(setSelectedIdWithDrawer(null))
      }
    }
  }, [currPath, prevPath, currKey, prevKey, dispatch, selectedId])

  useEffect(() => {
    if (doneLoading && currPath !== prevPath && histAction !== 'REPLACE') {
      pageView(currPath)
      notifications.forEach((notification) => {
        // Don't delete persistent notifications that should remain until programmed
        if (PERSISTENT_NOTIFICATION_IDS.includes(notification.id)) return
        if (
          [
            NOTIFICATION_ERROR,
            NOTIFICATION_SUCCESS,
            NOTIFICATION_WARNING,
          ].includes(notification.type)
        )
          dispatch(
            deleteSystemNotification({ notificationId: notification.id })
          )
      })
    }
  }, [doneLoading, currPath, prevPath, histAction, notifications, dispatch])
  useDynamicTitle()
  return (
    <AppRoot className="App">
      <IconContext.Provider value={{ style: { verticalAlign: 'middle' } }}>
        <NotificationProvider>
          <ErrorBoundary error={error} level={APP} signal={signals}>
            {isLoading ? (
              <Loading />
            ) : (
              <Suspense fallback={<Loading />}>
                <Routes />
              </Suspense>
            )}
          </ErrorBoundary>
        </NotificationProvider>
      </IconContext.Provider>
    </AppRoot>
  )
}

App.propTypes = {
  gaId: PropTypes.string,
}

export default App
